/**
 * ProgressSheet — expandable detail panel that slides up from the
 * ProgressStrip and overlays the chat transcript (but NOT the input area
 * or the strip itself). Renders the three-tier hierarchy:
 *
 *     ▼ Mapping                            ⏳ 7/11 · 64%
 *       ✓ clinical_data    4/4 cols · 2 rows
 *       ⏳ form_data       Validating join keys…
 *       ⚠ discrepancy_note no source coverage
 *       ◌ item_data        queued
 *     ▶ Quality Review                     ◌ pending
 *     ▶ Assembly                           ◌ pending
 *
 * Closed by clicking the strip toggle, the X in the header, or pressing
 * Escape while focus is inside the sheet.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  HourglassEmpty as RunningIcon,
  CheckCircle as DoneIcon,
  RadioButtonUnchecked as QueuedIcon,
  WarningAmber as NeedsInputIcon,
  ErrorOutline as FailedIcon,
} from '@mui/icons-material';
import type {
  ProgressPhase,
  ProgressState,
  ProgressTask,
  TaskState,
} from '../../../types/progress';

export interface ProgressSheetProps {
  progress: ProgressState;
  open: boolean;
  onClose: () => void;
}

const STATE_ICON: Record<TaskState, React.ElementType> = {
  queued: QueuedIcon,
  running: RunningIcon,
  done: DoneIcon,
  needs_input: NeedsInputIcon,
  failed: FailedIcon,
};

function StateGlyph({ state }: { state: TaskState }) {
  const theme = useTheme();
  const Icon = STATE_ICON[state];
  const color = (() => {
    switch (state) {
      case 'done':
        return theme.palette.success.main;
      case 'running':
        return theme.palette.primary.main;
      case 'needs_input':
        return theme.palette.warning.main;
      case 'failed':
        return theme.palette.error.main;
      default:
        return theme.palette.text.disabled;
    }
  })();
  return (
    <Icon
      sx={{
        fontSize: 14,
        color,
        flexShrink: 0,
        animation:
          state === 'running' ? 'progress-sheet-spin 1.4s linear infinite' : 'none',
        '@keyframes progress-sheet-spin': {
          '0%': { transform: 'rotate(0deg)' },
          '100%': { transform: 'rotate(360deg)' },
        },
      }}
    />
  );
}

function formatMetrics(task: ProgressTask): string {
  const m = task.metrics || {};
  const out: string[] = [];
  if (typeof m.cols_mapped === 'number') out.push(`${m.cols_mapped} cols`);
  else if (typeof m.cols === 'number') out.push(`${m.cols} cols`);
  if (typeof m.rows === 'number') out.push(`${m.rows} rows`);
  if (typeof m.duration_ms === 'number' && m.duration_ms > 0) {
    const s = Math.round((m.duration_ms as number) / 100) / 10;
    out.push(`${s}s`);
  }
  return out.join(' · ');
}

function TaskRow({ task }: { task: ProgressTask }) {
  const theme = useTheme();
  const detail = (() => {
    if (task.activity) return task.activity;
    if (task.reason) return task.reason;
    if (task.state === 'done') return formatMetrics(task);
    if (task.state === 'queued') return 'queued';
    return '';
  })();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        pl: 3,
        pr: 1.5,
        py: 0.5,
        minHeight: 24,
        bgcolor:
          task.state === 'needs_input'
            ? alpha(theme.palette.warning.main, 0.06)
            : task.state === 'failed'
            ? alpha(theme.palette.error.main, 0.06)
            : 'transparent',
      }}
    >
      <StateGlyph state={task.state} />
      <Typography
        variant="caption"
        sx={{
          flexShrink: 0,
          fontWeight: 500,
          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          fontSize: 11,
        }}
      >
        {task.label}
      </Typography>
      <Typography
        variant="caption"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'text.secondary',
          fontSize: 11,
        }}
        title={detail}
      >
        {detail}
      </Typography>
    </Box>
  );
}

function PhaseRow({ phase }: { phase: ProgressPhase }) {
  const theme = useTheme();
  const [open, setOpen] = useState(true);

  // Auto-collapse phases that haven't started yet (saves vertical space).
  useEffect(() => {
    if (phase.state === 'queued' && phase.tasks.length === 0) setOpen(false);
  }, [phase.state, phase.tasks.length]);

  const summary = (() => {
    if (phase.tasks.length === 0) {
      if (phase.state === 'done') return '';
      return 'pending';
    }
    if (phase.state === 'done') return `${phase.total}/${phase.total}`;
    const pct = phase.total > 0 ? Math.round((phase.done / phase.total) * 100) : 0;
    return `${phase.done}/${phase.total} · ${pct}%`;
  })();

  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) },
        }}
      >
        {open ? (
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        ) : (
          <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
        <StateGlyph state={phase.state} />
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 600, fontSize: 11.5 }}>
          {phase.label}
        </Typography>
        {summary && (
          <Typography
            variant="caption"
            sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary', fontSize: 11 }}
          >
            {summary}
          </Typography>
        )}
      </Box>
      <Collapse in={open} timeout="auto">
        {phase.tasks.map((t) => (
          <TaskRow key={t.id} task={t} />
        ))}
      </Collapse>
    </Box>
  );
}

const ProgressSheet: React.FC<ProgressSheetProps> = ({ progress, open, onClose }) => {
  const theme = useTheme();

  // Escape closes the sheet when it has focus.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const hasContent = useMemo(() => progress.phases.length > 0, [progress.phases.length]);

  if (!open) return null;

  return (
    <Box
      role="dialog"
      aria-label="Run progress details"
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        maxHeight: '70%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderTop: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
        zIndex: 5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1, fontSize: 12, fontWeight: 600 }}>
          Run progress
        </Typography>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ width: 22, height: 22 }} aria-label="Close progress details">
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {hasContent ? (
          progress.phases.map((p) => <PhaseRow key={p.id} phase={p} />)
        ) : (
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">
              No active run. Progress will appear here when the agent starts a task.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ProgressSheet;
